import { useState } from 'react';

import { Box, Button, Card, Flex, Text, Title, Tooltip } from '@mantine/core';

import { CreditSubscription, DesignerSubscription, SubscriptionType } from 'models';
import { SubscriptionConfigHelpers } from 'helpers/SubscriptionConfigHelpers';
import { SubscriptionHelpers } from 'helpers/SubscriptionHelpers';

import { SubscriptionUpdateCommonProps } from '../../types';
import { CreditPackageSelector } from '../CreditPackageSelector';
import { PriceTagStacked } from '../PriceTag';
import { SubscribeModalContentContainer } from '../SubscribeModalContentContainer';
import { SubscribeModalContent } from '../SubscribeModalContents';
import { UsageGuidancePanel } from '../UsageGuidancePanel';
import { useSubscriptionUpdate } from '../useSubscriptionUpdate';
import { Unsubscribe } from './Unsubscribe';

export const SubscribeForDesigner = ({
    toSubscription,
    ...subscriptionProps
}: {
    toSubscription: DesignerSubscription;
} & SubscriptionUpdateCommonProps) => {
    const { team } = subscriptionProps;

    const [selectedCreditAmount, setSelectedCreditAmount] = useState<number | null>(null);

    const creditQuantity = selectedCreditAmount ?? 0;
    const hasCreditPackageSelected = selectedCreditAmount !== null && selectedCreditAmount > 0;

    const additionalSubscriptions = [
        ...(hasCreditPackageSelected
            ? [
                  {
                      subscription: CreditSubscription.STANDARD,
                      quantity: creditQuantity,
                  },
              ]
            : []),
    ];

    const { submitSubscriptionUpdate, loading, ...updateStateAndController } = useSubscriptionUpdate({
        toSubscription,
        isDowngrade: false,
        additionalSubscriptions,
        quantity: 1,
        ...subscriptionProps,
    });

    if (toSubscription === DesignerSubscription.NONE) {
        return <Unsubscribe type={SubscriptionType.DESIGNER} toSubscription={toSubscription} {...subscriptionProps} />;
    }

    const currentSubscription = SubscriptionHelpers.getSubscriptionData(
        SubscriptionType.DESIGNER,
        team.subscriptions,
    ).subscription;

    const toSubscriptionIsSameAsCurrent = toSubscription === currentSubscription;

    const config = SubscriptionConfigHelpers.getSubscriptionConfigByType(SubscriptionType.DESIGNER)[toSubscription];

    return (
        <SubscribeModalContentContainer title={`Upgrade to ${config.title}`}>
            <SubscribeModalContent
                type={SubscriptionType.DESIGNER}
                toSubscription={toSubscription}
                additionalSubscriptions={additionalSubscriptions}
                {...subscriptionProps}
                {...updateStateAndController}
                quantity={1}
                SubmitButton={
                    <Tooltip disabled={!toSubscriptionIsSameAsCurrent} label="This is your current subscription.">
                        <Button
                            fullWidth
                            size="lg"
                            onClick={submitSubscriptionUpdate}
                            loading={loading}
                            disabled={currentSubscription === toSubscription}
                        >
                            {toSubscriptionIsSameAsCurrent ? 'Current Subscription' : 'Upgrade'}
                        </Button>
                    </Tooltip>
                }
            >
                <Card withBorder padding="lg" radius="md">
                    <Flex justify="space-between" align="flex-start">
                        <Box>
                            <Title order={3}>{config.title}</Title>
                            <Text c="dimmed" size="sm" mt={4}>
                                Priority support, advanced simulations, export options
                            </Text>
                        </Box>
                        <PriceTagStacked
                            subscription={toSubscription}
                            billingCycle={updateStateAndController.billingCycle}
                        />
                    </Flex>
                </Card>

                <Box>
                    <Text fw={600} fz={10} c="dimmed" tt="uppercase">
                        Add-ons
                    </Text>

                    <UsageGuidancePanel onSelectPreset={(amount) => setSelectedCreditAmount(amount)} />

                    <CreditPackageSelector
                        value={selectedCreditAmount}
                        onChange={setSelectedCreditAmount}
                        showNone
                        billingCycle={updateStateAndController.billingCycle}
                        PriceTag={
                            <>
                                {selectedCreditAmount ? (
                                    <PriceTagStacked
                                        multiplier={selectedCreditAmount}
                                        subscription={CreditSubscription.STANDARD}
                                        billingCycle={updateStateAndController.billingCycle}
                                    />
                                ) : (
                                    <Box ta="right">
                                        <Text size="lg" fw={700}>
                                            —
                                        </Text>
                                    </Box>
                                )}
                            </>
                        }
                    />

                    {/* {company && company.subscription !== CompanySubscription.PREMIUM && (
                        <Card withBorder padding="lg" radius="md" mt="xs">
                            <Flex justify="space-between" align="center">
                                <Checkbox
                                    checked={addCompanySubscription}
                                    onChange={(event) => setAddCompanySubscription(event.currentTarget.checked)}
                                    label={
                                        <Box>
                                            <Text fw={600}>Yes, boost my company profile</Text>
                                            <Text c="dimmed" size="sm">
                                                More visibility in search results
                                            </Text>
                                        </Box>
                                    }
                                />
                                <PriceTagStacked
                                    subscription={CompanySubscription.PREMIUM}
                                    billingCycle={updateStateAndController.billingCycle}
                                />
                            </Flex>
                        </Card>
                    )} */}
                </Box>
            </SubscribeModalContent>
        </SubscribeModalContentContainer>
    );
};
